import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPlatformSettings,
  updatePlatformSettings,
  type PlatformSettings,
  type UpdatePlatformSettingsRequest,
} from '../api/services/platform-config';

const QUERY_KEY = 'platform-settings';

/**
 * The platform settings document (GET /settings), typed by PlatformSettings.
 * Callers read isLoading and error: while the read is pending or after it
 * fails there is no document, and nothing should render in its place.
 */
export function useSettings() {
  return useQuery<PlatformSettings, Error>({
    queryKey: [QUERY_KEY],
    queryFn: getPlatformSettings,
    // A settings form is edited in place; a focus refetch would replace the
    // document under unsaved edits. The document changes on save, which
    // updates the cache directly.
    refetchOnWindowFocus: false,
  });
}

export function useUpdateSettings() {
  const queryClient = useQueryClient();
  return useMutation<PlatformSettings, Error, UpdatePlatformSettingsRequest>({
    mutationFn: updatePlatformSettings,
    onSuccess: (saved) => {
      queryClient.setQueryData([QUERY_KEY], saved);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}
